import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiUserPlus, FiMail, FiCheck, FiX, FiClock, FiTrash2, FiToggleLeft, FiToggleRight, FiEdit2 } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import showToast from '../../components/Toast';
import { usePermissions } from '../../contexts/PermissionContext';
import CustomDropdown from '../../components/CustomDropdown';
import './Team.css';

const Team = () => {
  const { hasPermission } = usePermissions();
  
  // Get user data from localStorage
  const getUserData = () => {
    try {
      const stored = localStorage.getItem('UserData');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  };

  const currentUser = getUserData();
  const userRole = config.getUserRole() || 'member';

  // Check if user can invite team members
  const canInviteMembers = () => {
    const allowedRoles = ['admin', 'owner', 'superadmin'];
    return allowedRoles.includes(userRole);
  };

  const [invitations, setInvitations] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
   const [showEditInviteModal, setShowEditInviteModal] = useState(false);
  const [editingInvitation, setEditingInvitation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  // Initialize invite form with current user's company name
  const getInitialInviteForm = () => {
    const userData = getUserData();
    const defaultCompanyName = userData?.CompanyName || '';
    return {
      emailAddress: '',
      firstName: '',
      lastName: '',
      companyName: defaultCompanyName,
      role: 'member'
    };
  };

  const [inviteForm, setInviteForm] = useState(getInitialInviteForm());
  const [availableRoles, setAvailableRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
  
  // Get userId from localStorage
  const userId = config.getUserId();

  const fetchInvitations = useCallback(async (inviterId) => {
    try {
      setLoading(true);
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.get(`${BASE_URL}/invitee/my-invitations?inviterId=${inviterId}`);
      
      // Handle new API response structure (same as Projects.js pattern)
      if (response.data && response.data.statusCode === 200) {
        setInvitations(response.data.data || []);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailableRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      
      // Get user's CompanyId
      const userData = getUserData();
      const userCompanyId = userData?.CompanyId;
      const isSuperAdmin = userRole === 'SuperAdmin' || userRole === 'superadmin';
      
      // Build API URL - backend will filter by CompanyId automatically for non-superadmins
      let apiUrl = `${BASE_URL}/role/all`;
      
      // For superadmins, we might want to show all roles or filter by selected company
      // For normal users, backend will automatically filter by their CompanyId
      const response = await AxiosInstance.get(apiUrl);
      
      if (response.data && response.data.statusCode === 200) {
        let rolesData = response.data.data || [];
        
        // Filter roles by user's CompanyId (for non-superadmins)
        // Backend already filters, but we do client-side filtering as well for safety
        if (!isSuperAdmin && userCompanyId) {
          const beforeFilter = rolesData.length;
          rolesData = rolesData.filter(role => 
            role.CompanyId === userCompanyId
          );
        }
        
        setAvailableRoles(rolesData);
      } else {
        setAvailableRoles([]);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setRolesLoading(false);
    }
  }, [userRole]);

  const fetchTeamMembers = useCallback(async (userId) => {
    try {
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.get(`${BASE_URL}/user/getallteammember/${userId}`);
      
      // Handle new API response structure (same as Projects.js pattern)
      if (response.data && response.data.statusCode === 200) {
        setTeamMembers(response.data.data || []);
      } else if (response.data && response.data.data) {
        // Fallback for old structure
        setTeamMembers(response.data.data || []);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      // Toast notification is handled by AxiosInstance interceptor
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    // Use userId for initial API calls
    fetchTeamMembers(userId);
    fetchInvitations(userId);
    fetchAvailableRoles();
  }, [userId, fetchTeamMembers, fetchInvitations, fetchAvailableRoles]);

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has permission to create invitations
    if (!hasPermission('canCreateInvitation')) {
      showToast.error('You do not have permission to create invitations');
      return;
    }
    
    if (!inviteForm.emailAddress || !inviteForm.firstName || !inviteForm.lastName || !inviteForm.role) {
      showToast.error('Please fill in all required fields including role');
      return;
    }

    // Check if we have currentUser data
    if (!currentUser || !currentUser.FirstName || !currentUser.LastName) {
      showToast.error('User data not available. Please refresh the page.');
      return;
    }

    try {
      setInviteLoading(true);
      
      // Find the selected role object
      const selectedRole = availableRoles.find(role => role.RoleId === inviteForm.role);
      
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.post(`${BASE_URL}/invitee/invite`, {
        inviterId: userId,
        inviterName: `${currentUser.FirstName} ${currentUser.LastName}`,
        emailAddress: inviteForm.emailAddress,
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName,
        companyName: inviteForm.companyName,
        roleId: inviteForm.role,
        roleName: selectedRole ? selectedRole.RoleName : 'member'
      });

      // Handle new API response structure (same as Projects.js pattern)
      if (response.data && response.data.statusCode === 201) {
        showToast.success(response.data.message || 'Invitation sent successfully!');
        setShowInviteModal(false);
        setInviteForm({
          emailAddress: '',
          firstName: '',
          lastName: '',
          companyName: '',
          role: 'member'
        });
        fetchInvitations(userId);
      } else {
        showToast.error(response.data?.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (inviteeId) => {
    // Check if user has permission to cancel invitations
    if (!hasPermission('canCancelInvitations')) {
      showToast.error('You do not have permission to cancel invitations');
      return;
    }
    
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.delete(`${BASE_URL}/invitee/cancel/${inviteeId}?inviterId=${userId}`);
      if (response.status === 200) {
        showToast.success('Invitation cancelled successfully');
        fetchInvitations(userId);
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      // Toast notification is handled by AxiosInstance interceptor
    }
  };

  const handleEditInvitation = (invitation) => {
    // Check if user has permission to edit invitations
    if (!hasPermission('canEditInvitations')) {
      showToast.error('You do not have permission to edit invitations');
      return;
    }
    
    setEditingInvitation(invitation);
    
    // Find the role by RoleName or RoleId from invitation
    const invitationRole = invitation.Role || invitation.RoleName || 'member';
    const matchingRole = availableRoles.find(r => 
      r.RoleName === invitationRole || 
      r.RoleId === invitationRole ||
      r.RoleId === invitation.RoleId
    );
    
    setInviteForm({
      emailAddress: invitation.EmailAddress,
      firstName: invitation.FirstName,
      lastName: invitation.LastName,
      companyName: invitation.CompanyName || '',
      role: matchingRole ? matchingRole.RoleId : (invitation.RoleId || '')
    });
    setShowEditInviteModal(true);
  };

  const handleUpdateInvitation = async (e) => {
    e.preventDefault();
    
    // Check if user has permission to edit invitations
    if (!hasPermission('canEditInvitations')) {
      showToast.error('You do not have permission to edit invitations');
      return;
    }
    
    if (!editingInvitation) return;

    setInviteLoading(true);
    try {
      // Find the selected role object
      const selectedRole = availableRoles.find(role => role.RoleId === inviteForm.role);
      
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.put(`${BASE_URL}/invitee/edit/${editingInvitation.InviteeId}`, {
        emailAddress: inviteForm.emailAddress,
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName,
        companyName: inviteForm.companyName,
        roleId: inviteForm.role,
        roleName: selectedRole ? selectedRole.RoleName : (editingInvitation.Role || 'member')
      });

      if (response.data.statusCode === 200) {
        showToast.success('Invitation updated successfully!');
        setShowEditInviteModal(false);
        setEditingInvitation(null);
        resetInviteForm();
        fetchInvitations(userId);
      } else {
        showToast.error(response.data.message || 'Failed to update invitation');
      }
    } catch (error) {
      console.error('Error updating invitation:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update invitation';
      showToast.error(errorMessage);
    } finally {
      setInviteLoading(false);
    }
  };

  const resetInviteForm = () => {
    // Get current user's company name to pre-fill
    const userData = getUserData();
    const defaultCompanyName = userData?.CompanyName || '';
    
    setInviteForm({
      emailAddress: '',
      firstName: '',
      lastName: '',
      companyName: defaultCompanyName,
      role: 'member'
    });
  };

  const handleCloseEditModal = () => {
    setShowEditInviteModal(false);
    setEditingInvitation(null);
    resetInviteForm();
  };


  const handleDeleteUser = async (userIdToDelete) => {
    // Check if user has permission to cancel invitations
    if (!hasPermission('canCancelInvitations')) {
      showToast.error('You do not have permission to cancel invitations');
      return;
    }

    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) return;
    
    try {
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.delete(`${BASE_URL}/user/deleteuser/${userIdToDelete}`);
      if (response.status === 200) {
        showToast.success('User deleted successfully');
        fetchTeamMembers(userId); // Refresh the list
      } else {
        showToast.error('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      // Toast notification is handled by AxiosInstance interceptor
    }
  };

  const handleEditMember = async (userIdToEdit) => {
    // Check if user has permission to edit invitations
    if (!hasPermission('canEditInvitations')) {
      showToast.error('You do not have permission to edit invitations');
      return;
    }

    // TODO: Implement edit member functionality
    showToast.info('Edit member functionality coming soon...');
  };

  const handleToggleFlag = async (userId, flag, value) => {
    try {
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.put(`${BASE_URL}/user/toggle-flag/${userId}`, {
        [flag]: value
      });
      if (response.status === 200) {
        showToast.success('User flag updated');
        fetchTeamMembers(userId);
      } else {
        showToast.error('Failed to update user flag');
      }
    } catch (error) {
      console.error('Error updating user flag:', error);
      // Toast notification is handled by AxiosInstance interceptor
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'orange', icon: <FiClock />, text: 'Pending' },
      accepted: { color: 'green', icon: <FiCheck />, text: 'Accepted' },
      declined: { color: 'red', icon: <FiX />, text: 'Declined' },
      expired: { color: 'gray', icon: <FiClock />, text: 'Expired' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`status-badge status-${status}`} style={{ backgroundColor: config.color }}>
        {config.icon}
        {config.text}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    // Capitalize first letter of role
    const capitalizedRole = role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() : 'Member';
    const roleClass = role ? role.toLowerCase() : 'member';
    return (
      <span className={`role-badge ${roleClass}`}>
        {capitalizedRole}
      </span>
    );
  };



  return (
    <div className="team-container">
      <div className="team-header">
        <div className="team-header-left">
          <FiUsers className="team-icon" />
          <h1>Team Management</h1>
        </div>
        {hasPermission('canCreateInvitation') && (
          <button 
            className="invite-button"
            onClick={() => {
              // Pre-fill company name from current user
              const userData = getUserData();
              const defaultCompanyName = userData?.CompanyName || '';
              setInviteForm(prev => ({
                ...prev,
                companyName: defaultCompanyName
              }));
              setShowInviteModal(true);
              fetchAvailableRoles(); // Fetch roles when modal opens
            }}
          >
            <FiUserPlus />
            Invite Team Member
          </button>
        )}
      </div>

      <div className="team-content">
        <div className="team-stats">
          <div className="stat-card">
            <h3>Total Invitations</h3>
            <p>{invitations.length}</p>
          </div>
          <div className="stat-card">
            <h3>Pending</h3>
            <p>{invitations.filter(inv => inv.Status === 'pending').length}</p>
          </div>
          <div className="stat-card">
            <h3>Accepted</h3>
            <p>{invitations.filter(inv => inv.Status === 'accepted').length}</p>
          </div>
          <div className="stat-card">
            <h3>Total Members</h3>
            <p>{teamMembers.length}</p>
          </div>
        </div>

        

        <div className="invitations-section">
          <h2>Invitations</h2>
          {!hasPermission('canCreateInvitation') && (
            <div className="permission-notice">
              <p>Only team administrators can send invitations to new team members.</p>
            </div>
          )}
          {loading ? (
            <div className="loading">Loading invitations...</div>
          ) : invitations.length === 0 ? (
            <div className="empty-state">
              <FiUsers size={48} />
              <h3>No invitations yet</h3>
              <p>Start building your team by inviting members</p>
              {hasPermission('canCreateInvitation') && (
                <button 
                  className="invite-button"
                  onClick={() => {
                    // Pre-fill company name from current user
                    const userData = getUserData();
                    const defaultCompanyName = userData?.CompanyName || '';
                    setInviteForm(prev => ({
                      ...prev,
                      companyName: defaultCompanyName
                    }));
                    setShowInviteModal(true);
                  }}
                >
                  <FiUserPlus />
                  Send First Invitation
                </button>
              )}
            </div>
          ) : (
            <div className="invitations-list">
              {invitations.map((invitation) => (
                <div key={invitation.InviteeId} className="invitation-card">
                  <div className="invitation-info">
                    <div className="invitation-header">
                      <h4>{invitation.FirstName} {invitation.LastName}</h4>
                      {getRoleBadge(invitation.Role)}
                    </div>
                    <p className="invitation-email">
                      <FiMail />
                      {invitation.EmailAddress}
                    </p>
                    {invitation.CompanyName && (
                      <p className="invitation-company">
                        <strong>Company:</strong> {invitation.CompanyName}
                      </p>
                    )}
                    <div className="invitation-meta">
                      <span>Invited by: {invitation.InviterName}</span>
                      <span>Date: {new Date(invitation.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="invitation-actions">
                    {getStatusBadge(invitation.Status)}
                    {(invitation.Status === 'pending' || invitation.Status === 'expired') && (
                      <>
                        {hasPermission('canEditInvitations') && (
                          <button
                            className="edit-button"
                            onClick={() => handleEditInvitation(invitation)}
                            title="Edit invitation"
                          >
                            <FiEdit2 />
                          </button>
                        )}
                        {hasPermission('canCancelInvitations') && (
                          <button
                            className="cancel-button"
                            onClick={() => handleCancelInvitation(invitation.InviteeId)}
                            title="Cancel invitation"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Invite Team Member</h3>
              <button 
                className="modal-close"
                onClick={() => setShowInviteModal(false)}
              >
                <FiX />
              </button>
            </div>
            <form onSubmit={handleInviteSubmit} className="invite-form">
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={inviteForm.emailAddress}
                  onChange={(e) => setInviteForm({...inviteForm, emailAddress: e.target.value})}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={inviteForm.firstName}
                    onChange={(e) => setInviteForm({...inviteForm, firstName: e.target.value})}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={inviteForm.lastName}
                    onChange={(e) => setInviteForm({...inviteForm, lastName: e.target.value})}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={inviteForm.companyName}
                  onChange={(e) => setInviteForm({...inviteForm, companyName: e.target.value})}
                  placeholder="Enter company name (optional)"
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                {rolesLoading ? (
                  <div className="loading-text">Loading roles...</div>
                ) : (
                  <CustomDropdown
                    options={availableRoles.map(role => ({
                      value: role.RoleId,
                      label: `${role.RoleDisplayName || role.RoleName}${role.Description ? ` - ${role.Description}` : ''}`,
                      data: role
                    }))}
                    value={inviteForm.role ? {
                      value: inviteForm.role,
                      label: availableRoles.find(r => r.RoleId === inviteForm.role)?.RoleDisplayName || 
                             availableRoles.find(r => r.RoleId === inviteForm.role)?.RoleName || 
                             'Select a role'
                    } : null}
                    onChange={(option) => {
                      setInviteForm({...inviteForm, role: option?.value || ''});
                    }}
                    placeholder="Select a role"
                    className="role-dropdown"
                    searchable={true}
                    textColor="white"
                  />
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-save-btn"
                  disabled={inviteLoading}
                >
                  {inviteLoading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Invitation Modal */}
      {showEditInviteModal && editingInvitation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Invitation</h3>
              <button 
                className="modal-close-btn"
                onClick={handleCloseEditModal}
              >
                <FiX />
              </button>
            </div>
            <form onSubmit={handleUpdateInvitation}>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={inviteForm.emailAddress}
                  onChange={(e) => setInviteForm({...inviteForm, emailAddress: e.target.value})}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={inviteForm.firstName}
                    onChange={(e) => setInviteForm({...inviteForm, firstName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={inviteForm.lastName}
                    onChange={(e) => setInviteForm({...inviteForm, lastName: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={inviteForm.companyName}
                  onChange={(e) => setInviteForm({...inviteForm, companyName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                {rolesLoading ? (
                  <div className="loading-text">Loading roles...</div>
                ) : (
                  <CustomDropdown
                    options={availableRoles.map(role => ({
                      value: role.RoleId,
                      label: `${role.RoleDisplayName || role.RoleName}${role.Description ? ` - ${role.Description}` : ''}`,
                      data: role
                    }))}
                    value={inviteForm.role ? {
                      value: inviteForm.role,
                      label: availableRoles.find(r => r.RoleId === inviteForm.role)?.RoleDisplayName || 
                             availableRoles.find(r => r.RoleId === inviteForm.role)?.RoleName || 
                             'Select a role'
                    } : null}
                    onChange={(option) => {
                      setInviteForm({...inviteForm, role: option?.value || ''});
                    }}
                    placeholder="Select a role"
                    className="role-dropdown"
                    searchable={true}
                    textColor="white"
                  />
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={handleCloseEditModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-save-btn"
                  disabled={inviteLoading}
                >
                  {inviteLoading ? 'Updating...' : 'Update Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;